import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client/core';
import { FeatureFlagTypes } from '@generated/graphql';
import {
  IntegrationProviders,
  LinkTokenConfig,
} from '@interfaces/integrations';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { DialogContent } from '@mui/material';
import { DialogTitle } from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import {
  UserContext,
  useOrganizationFeatureFlag,
} from '@providers/userContextProvider';
import { generateFlinksUrl } from '@utils/bankAccount';
import { isValidFlinksUrl } from '@utils/common';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidEmbeddedLink } from 'react-plaid-link';
import { findIntegrationCondition } from '../findIntegrationCondition';

const FETCH_USER_BANK_ACCOUNT_IDS = gql`
  query fetchBankAccounts($input: FetchBankAccountsInput!) {
    fetchBankAccounts(input: $input) {
      bankAccounts {
        id
      }
    }
  }
`;

const CREATE_LINK_TOKEN = gql`
  mutation createLinkToken($input: CreateLinkTokenInput!) {
    createLinkToken(input: $input) {
      linkToken
    }
  }
`;

export const CREATE_BANK_CONNECTION = gql`
  mutation createBankConnection($input: CreateBankConnectionInput!) {
    createBankConnection(input: $input) {
      bankConnection {
        id
        loginId
        bankAccounts {
          id
          name
          institutionNumber
          transitNumber
          bankAccountNumber
        }
      }
    }
  }
`;

type Credentials = {
  loginId?: string;
  bankAccountRefId?: string;
};

const AddBankAccountAutomaticallyModal = ({
  onNext,
  onCancel,
  bankAccounts,
  userId,
  afterCreate,
  options,
}: {
  onNext: (index: number) => void;
  onCancel: () => void;
  bankAccounts: { id: string; name: string; bankAccountNumber: string }[];
  afterCreate: (newBankAccountId: string) => void;
  userId: string;
  transactionType?: 'DEPOSIT' | 'WITHDRAWAL';
  options?: any;
}) => {
  const { t } = useTranslation(['components']);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { activeOrganization, integrations, activeLanguage } =
    useContext(UserContext);
  const manuallyAddBankAccountEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ManuallyAddBankAccount,
  );

  const [credentials, setCredentials] = useState<Credentials>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchingBank, setIsFetchinBank] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const bankingIntegration = integrations?.find(
    findIntegrationCondition(options),
  );
  const hasFlinksIntegration =
    bankingIntegration &&
    bankingIntegration.provider === IntegrationProviders.FLINKS;
  const hasPlaidIntegration =
    bankingIntegration &&
    bankingIntegration.provider === IntegrationProviders.PLAID;

  const integrationClientIframeUrl =
    bankingIntegration?.configuration?.clientIframeUrl ?? '';
  const enableManualBankAccount: boolean =
    options?.manualbankAccountLinking ?? manuallyAddBankAccountEnabled ?? false;

  const getIframeUrl = (): string => {
    if (hasFlinksIntegration) {
      return generateFlinksUrl(
        integrationClientIframeUrl,
        activeOrganization?.productShelfCurrencies,
        activeLanguage,
      );
    }

    return integrationClientIframeUrl;
  };

  const { refetch } = useQuery(FETCH_USER_BANK_ACCOUNT_IDS, {
    variables: { input: { filter: { userId } } },
  });

  const fetchBanksAtInterval = async (): Promise<void> => {
    const interval = setInterval(async () => {
      const result = await refetch();
      const allBanks = result.data.fetchBankAccounts.bankAccounts ?? [];
      const distinctBankAccounts = allBanks.filter(
        (ba: { id: string }) => !bankAccounts.some((old) => ba.id === old.id),
      );
      if (distinctBankAccounts.length > 0) {
        clearInterval(interval);
        const latestAccount =
          distinctBankAccounts[distinctBankAccounts.length - 1];
        afterCreate(latestAccount.id);
        onCancel();
      }
    }, 5000);
    setTimeout(() => {
      onCancel();
      clearInterval(interval);
    }, 300000);
  };

  const [createBankConnectionMutation] = useMutation(CREATE_BANK_CONNECTION, {
    variables: {
      input: {
        loginId: credentials.loginId,
        bankAccountRefId: credentials.bankAccountRefId?.trim() || undefined,
        userId,
        ...(hasPlaidIntegration && { provider: IntegrationProviders.PLAID }),
        ...(hasFlinksIntegration && { provider: IntegrationProviders.FLINKS }),
      },
    },
  });

  const [createLinkToken] = useMutation(CREATE_LINK_TOKEN, {
    variables: {
      input: {
        userId,
        clientId: bankingIntegration?.configuration?.customerId,
        plaidSecret: bankingIntegration?.configuration?.plaidSecret,
        configType: LinkTokenConfig.BASELINE,
      },
    },
  });

  useEffect(() => {
    if (hasPlaidIntegration === false) return;
    const fetchLinkToken = async () => {
      if (!hasPlaidIntegration) return;

      const token = await createLinkToken();
      setLinkToken(token.data?.createLinkToken?.linkToken ?? null);
      setIsLoading(false);
    };
    fetchLinkToken();
  }, []);

  useEffect(() => {
    if (hasFlinksIntegration === false) return;

    window.addEventListener('message', (e) => {
      if (e.data.step === 'REDIRECT') {
        setIsFetchinBank(true);
        fetchBanksAtInterval();
      }
      if (e.data.step === 'APP_MOUNTED') setIsLoading(false);
      if (
        e.origin === 'https://onevest9163.connect.flinks.dev' ||
        e.origin ===
          (integrationClientIframeUrl ??
            'https://onevest-iframe.private.fin.ag') ||
        isValidFlinksUrl(e.origin, integrationClientIframeUrl)
      ) {
        if (e.data.step && e.data.step.toLowerCase() === 'redirect') {
          setCredentials({
            loginId: e.data.loginId ?? '',
            bankAccountRefId: e.data.accountId,
          });
          fetchBanksAtInterval().then();
        }
      }
    });
  }, [onCancel]);

  useEffect(() => {
    if (credentials.loginId?.trim()) {
      setIsFetchinBank(true);
      createBankConnectionMutation().then(() => {
        enqueueSnackbar(
          t(
            'components:bankAccountSelect.bankAccountAddedIntegrationToastMessage',
          ),
          { variant: 'success' },
        );
        fetchBanksAtInterval().then();
      });
    }
  }, [credentials.loginId, createBankConnectionMutation]);

  return (
    <Form>
      <DialogTitle
        borderBottom='none'
        display='flex'
        justifyContent='end'
        onClose={onCancel}
      />
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: '20px',
          height: '550px',
          width: '550px',
        }}
      >
        {!isFetchingBank && hasFlinksIntegration && (
          <iframe
            title='ov-flinks'
            src={getIframeUrl()}
            style={{
              height: '550px',
              width: '550px',
              border: 0,
              visibility: isLoading ? 'hidden' : 'visible',
            }}
          />
        )}
        {!isFetchingBank && hasPlaidIntegration && linkToken && (
          <PlaidEmbeddedLink
            token={linkToken}
            onSuccess={(publicToken) => {
              setCredentials({ loginId: publicToken ?? '' });
            }}
            onExit={(error) => {
              if (error) {
                enqueueSnackbar(
                  t(
                    'components:bankAccountSelect.bankAccountAddedToastMessageError',
                  ),
                  { variant: 'error' },
                );
              }
            }}
            style={{
              height: '550px',
              width: '550px',
              border: 0,
              visibility: isLoading ? 'hidden' : 'visible',
            }}
          />
        )}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              position: 'absolute',
              justifyContent: 'center',
              width: '100%',
              top: '45%',
              bottom: '45%',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {isFetchingBank && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '550px',
              paddingX: '40px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '40px',
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant='labelLarge' sx={{ mb: 3 }}>
                {t('components:bankAccountSelect.flinks.linkingTimeInfo')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: theme.borderRadius.sm,
                padding: '20px',
                border: `1px solid ${theme.color.outlineVariant}`,
              }}
            >
              <InfoRoundedIcon color='primary' sx={{ marginRight: 2 }} />
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                {t(
                  'components:bankAccountSelect.flinks.linkingRedirectionInfo',
                )}
              </Typography>
            </Box>
          </Box>
        )}
        {enableManualBankAccount && !isFetchingBank && (
          <Button
            onClick={() => onNext(3)}
            variant='text'
            sx={{
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {t('components:bankAccountSelect.flinks.addManually')}
          </Button>
        )}
      </DialogContent>
    </Form>
  );
};

export default AddBankAccountAutomaticallyModal;
